/**
 * Analytics Tracker — Phase 17.2.1
 *
 * Lightweight event tracking for product analytics.
 * Pure logic: event definitions, batching, queue management.
 * Supabase integration is thin and isolated.
 *
 * Usage:
 *   trackEvent('novel_created', { genre: 'fantasy' });
 *   trackEvent('chapter_published', { novel_id: '...', word_count: 3000 });
 */

// ===== Event Catalog =====

/**
 * All tracked event names. Strict union type prevents typos.
 */
export type AnalyticsEventName =
  // Auth events
  | 'user_registered'
  | 'user_logged_in'
  // Novel lifecycle
  | 'novel_created'
  | 'novel_deleted'
  // Writing milestones
  | 'chapter_published'
  | 'chapter_unpublished'
  | 'novel_exported'
  // AI usage
  | 'ai_generation_started'
  | 'ai_generation_completed'
  | 'ai_generation_failed'
  // Feature usage
  | 'codex_entry_created'
  | 'outline_modified'
  | 'foundation_completed'
  | 'seed_generated'
  // Navigation / engagement
  | 'page_viewed'
  | 'novel_bookmarked'
  | 'review_submitted'
  // Reading funnel (Phase 18)
  | 'plaza_viewed'
  | 'novel_card_clicked'
  | 'novel_detail_viewed'
  | 'chapter_list_clicked'
  | 'reader_opened'
  | 'reader_chapter_nav'
  | 'reader_exited'
  // Reading depth (Phase 18)
  | 'reading_progress_tick'
  | 'reading_completed'
  | 'reading_speed_sample'
  // Reading interactions (Phase 18)
  | 'font_size_changed'
  | 'comment_submitted'
  | 'share_card_created';

/**
 * Property schemas for each event. Keeps tracking consistent.
 */
/** Exit type when leaving the reader */
export type ReaderExitType = 'back_to_detail' | 'back_to_plaza' | 'browser_back' | 'tab_close' | 'nav_away' | 'chapter_nav';

/** How the reader was entered */
export type ReaderEntrySource = 'detail_chapter_list' | 'detail_continue' | 'prev_chapter' | 'next_chapter' | 'direct_link';

export interface EventPropertyMap {
  user_registered: { method?: 'email' | 'github' | 'google' };
  user_logged_in: { method?: 'email' | 'github' | 'google' };
  novel_created: { genre?: string; title_length?: number };
  novel_deleted: { novel_id: string; word_count?: number };
  chapter_published: { novel_id: string; word_count: number; chapter_index?: number };
  chapter_unpublished: { novel_id: string; chapter_id: string };
  novel_exported: { format: 'txt' | 'epub' | 'pdf'; chapter_count: number };
  ai_generation_started: { mode: string; model?: string };
  ai_generation_completed: { mode: string; tokens?: number; duration_ms?: number };
  ai_generation_failed: { mode: string; error?: string };
  codex_entry_created: { category: string };
  outline_modified: { action: 'add' | 'delete' | 'reorder' | 'move'; count?: number };
  foundation_completed: { section: string };
  seed_generated: { genre?: string };
  page_viewed: { page: string; referrer?: string };
  novel_bookmarked: { novel_id: string };
  review_submitted: { novel_id: string; rating: number };
  // Reading funnel
  plaza_viewed: { tab?: string; genre_filter?: string; source?: string };
  novel_card_clicked: { novel_id: string; position?: number; genre?: string; source_tab?: string };
  novel_detail_viewed: { novel_id: string; genre?: string; chapter_count?: number; word_count?: number; has_cover?: boolean };
  chapter_list_clicked: { novel_id: string; chapter_id: string; chapter_index: number; is_first_chapter: boolean };
  reader_opened: { novel_id: string; chapter_id: string; chapter_index?: number; word_count?: number; entry_source: ReaderEntrySource };
  reader_chapter_nav: { novel_id: string; from_chapter: string; to_chapter: string; direction: 'prev' | 'next'; reading_time_ms: number };
  reader_exited: { novel_id: string; chapter_id: string; exit_type: ReaderExitType; scroll_pct: number; reading_time_ms: number; word_count?: number };
  // Reading depth
  reading_progress_tick: { novel_id: string; chapter_id: string; scroll_pct: number; visible_time_ms: number; is_active: boolean };
  reading_completed: { novel_id: string; chapter_id: string; total_time_ms: number; word_count: number; reading_speed_wpm: number };
  reading_speed_sample: { novel_id: string; chapter_id: string; word_count: number; total_time_ms: number; wpm: number; scroll_depth_pct: number };
  // Reading interactions
  font_size_changed: { novel_id?: string; old_size: number; new_size: number };
  comment_submitted: { novel_id: string; chapter_id: string; content_length: number };
  share_card_created: { novel_id: string };
}

// ===== Queue and Batching =====

export interface QueuedEvent {
  event_name: AnalyticsEventName;
  properties: Record<string, unknown>;
  timestamp: number;
}

/** Maximum events to buffer before forcing a flush */
export const MAX_QUEUE_SIZE = 20;

/** Default flush interval in ms (30 seconds) */
export const FLUSH_INTERVAL_MS = 30_000;

/** Maximum property value length to prevent abuse */
export const MAX_PROPERTY_VALUE_LENGTH = 500;

/**
 * Sanitize event properties: truncate long strings, remove undefined.
 */
export function sanitizeProperties(props: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(props)) {
    if (value === undefined) continue;
    if (typeof value === 'string' && value.length > MAX_PROPERTY_VALUE_LENGTH) {
      result[key] = value.slice(0, MAX_PROPERTY_VALUE_LENGTH);
    } else {
      result[key] = value;
    }
  }
  return result;
}

/**
 * Check if an event name is valid (in the catalog).
 */
export function isValidEventName(name: string): name is AnalyticsEventName {
  return VALID_EVENT_NAMES.has(name);
}

/** Set of all valid event names for O(1) lookup */
const VALID_EVENT_NAMES = new Set<string>([
  'user_registered', 'user_logged_in',
  'novel_created', 'novel_deleted',
  'chapter_published', 'chapter_unpublished', 'novel_exported',
  'ai_generation_started', 'ai_generation_completed', 'ai_generation_failed',
  'codex_entry_created', 'outline_modified', 'foundation_completed', 'seed_generated',
  'page_viewed', 'novel_bookmarked', 'review_submitted',
  // Reading funnel
  'plaza_viewed', 'novel_card_clicked', 'novel_detail_viewed',
  'chapter_list_clicked', 'reader_opened', 'reader_chapter_nav', 'reader_exited',
  // Reading depth
  'reading_progress_tick', 'reading_completed', 'reading_speed_sample',
  // Reading interactions
  'font_size_changed', 'comment_submitted', 'share_card_created',
]);

/**
 * Generate a random session ID for grouping events within a browser session.
 */
export function generateSessionId(): string {
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 8);
  return `s_${ts}_${rand}`;
}

// ===== Event Queue (pure logic) =====

/**
 * Create a new event queue. Returns queue management functions.
 * The `onFlush` callback receives batched events for persistence.
 */
export function createEventQueue(onFlush: (events: QueuedEvent[]) => void) {
  let queue: QueuedEvent[] = [];
  let timer: ReturnType<typeof setInterval> | null = null;

  function enqueue(event: QueuedEvent): void {
    queue.push(event);
    if (queue.length >= MAX_QUEUE_SIZE) {
      flush();
    }
  }

  function flush(): void {
    if (queue.length === 0) return;
    const batch = queue.slice();
    queue = [];
    onFlush(batch);
  }

  function start(): void {
    if (timer) return;
    timer = setInterval(flush, FLUSH_INTERVAL_MS);
  }

  function stop(): void {
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
    flush(); // flush remaining on stop
  }

  function size(): number {
    return queue.length;
  }

  return { enqueue, flush, start, stop, size };
}